import { CommunityId, daysAgo, ObservationFreshness } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import {
    EvergreenCommunityListingAvailability,
    FindOrCreateCommunityInsightsMutation,
} from "generated/graphql";
import React, { useCallback } from "react";
import { useCanRequestCommunityAvailability } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { CommunityRequestAvailabilityUpdatePanel } from "./community-request-availability-update-panel";

interface CommunityAvailabilityExistingProps {
    listing: FindOrCreateCommunityInsightsMutation["findOrCreateCommunityInsights"];
    communityId: CommunityId;
}

export const CommunityAvailabilityExisting = (
    props: CommunityAvailabilityExistingProps,
) => {
    const { listing, communityId } = props;
    const canRequestAvailability = useCanRequestCommunityAvailability();
    const [isRequestUpdateDialogOpen, setIsRequestUpdateDialogOpen] =
        React.useState(false);
    const [hasRequestedUpdate, setHasRequestedUpdate] = React.useState(false);

    const requestUpdate = useCallback(() => {
        handleEvent("request-community-availability-update", {
            communityId,
            listingId: listing.id,
        });
        setIsRequestUpdateDialogOpen(true);
    }, [communityId, listing.id]);

    const closeRequestUpdateDialog = useCallback(() => {
        setIsRequestUpdateDialogOpen(false);
    }, []);

    const onRequestUpdateSuccess = useCallback(() => {
        setHasRequestedUpdate(true);
        closeRequestUpdateDialog();
        handleEvent("request-community-availability-update-success", {
            communityId,
            listingId: listing.id,
        });
    }, [closeRequestUpdateDialog, communityId, listing.id]);

    const showRequestUpdateExperience =
        listing.availability?.freshness !== ObservationFreshness.Fresh &&
        canRequestAvailability;

    return (
        <>
            {isRequestUpdateDialogOpen ? (
                <CommunityRequestAvailabilityUpdatePanel
                    communityId={communityId}
                    communityName={listing.name}
                    availabilityStatus={
                        listing.availability?.status ||
                        EvergreenCommunityListingAvailability.Unknown
                    }
                    onDismiss={closeRequestUpdateDialog}
                    onSuccess={onRequestUpdateSuccess}
                />
            ) : null}
            <div className="space-y-2">
                {showRequestUpdateExperience ? (
                    <div className="space-y-2">
                        <InlineBanner type="warning">
                            <div className="space-y-4">
                                <div>
                                    The availability information below has not been
                                    updated recently. To ensure accuracy, request an
                                    update from the community.
                                </div>
                                <PrimaryButton
                                    onClick={requestUpdate}
                                    className="text-sm">
                                    Request Update
                                </PrimaryButton>
                            </div>
                        </InlineBanner>
                    </div>
                ) : null}
                {listing.availability?.status ===
                EvergreenCommunityListingAvailability.Available ? (
                    <InlineBanner type="success">
                        {listing.name} has a vacancy.
                    </InlineBanner>
                ) : (
                    <InlineBanner type="error">
                        {listing.name} does not have a vacancy at this time.
                    </InlineBanner>
                )}
                {listing.availability?.notes ? (
                    <div className="space-y-1">
                        <p className="font-medium text-gray-500">Additional Notes</p>
                        <p className="text-gray-600">{listing.availability.notes}</p>
                    </div>
                ) : null}

                {hasRequestedUpdate ? (
                    <InlineBanner type="info">
                        Availability update request sent!
                    </InlineBanner>
                ) : null}
                <div className="mt-4">
                    <p className="text-gray-500 text-sm">
                        Last updated {daysAgo(listing.availability?.lastUpdatedAt)}.
                    </p>
                </div>
            </div>
        </>
    );
};
